import React, { useState } from 'react';
import {
    Button,
    Card,
    Typography,
    Input,
    Select,
    Option
} from '@material-tailwind/react';
import { FaFilter, FaSyncAlt, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';

const TransMessagesFilters = ({
    filters,
    onFiltersChange,
    onRefresh,
    loading,
    sortBy,
    sortOrder,
    onSortChange,
    totalMessages = 0,
    visibleCount = 0
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const handleFilterChange = (key, value) => {
        onFiltersChange(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        onFiltersChange({
            status: '',
            message_type: '',
            car_id: '',
            driver_id: '',
            amount_min: '',
            amount_max: '',
            date_from: '',
            date_to: '',
            content: '',
            posting_status: ''
        });
    };

    return (
        <>
            <div className="mb-4 sm:mb-6">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white border border-gray-200 rounded-sm p-2 sm:p-3 shadow-none">
                    <div className="flex flex-wrap gap-2 sm:justify-start justify-end w-full sm:w-auto">
                        <Button
                            onClick={() => setShowFilters(!showFilters)}
                            variant="outlined"
                            size="sm"
                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case"
                        >
                            <FaFilter /> Filters
                        </Button>
                        <Button
                            onClick={() => onRefresh()}
                            size="sm"
                            disabled={loading}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                        >
                            <FaSyncAlt className={loading ? 'animate-spin' : ''} />
                            <span>Refresh</span>
                        </Button>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 sm:justify-end justify-end w-full sm:w-auto flex-wrap">
                        <Typography variant="small" color="blue-gray" className="font-medium">
                            Sort by:
                        </Typography>
                        <Button
                            variant="text"
                            size="sm"
                            onClick={() => onSortChange('created_at')}
                            className={`flex items-center gap-1 normal-case ${sortBy === 'created_at' ? 'text-[#FF5733] font-semibold' : 'text-slate-700'}`}
                        >
                            Date {sortBy === 'created_at' && (sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
                        </Button>
                        <Button
                            variant="text"
                            size="sm"
                            onClick={() => onSortChange('amount')}
                            className={`flex items-center gap-1 normal-case ${sortBy === 'amount' ? 'text-[#FF5733] font-semibold' : 'text-slate-700'}`}
                        >
                            Amount {sortBy === 'amount' && (sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                    <Typography variant="small" color="blue-gray" className="font-medium">
                        Showing {visibleCount} of {totalMessages} messages
                    </Typography>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <Card className="p-4 sm:p-6 mb-4 sm:mb-6 bg-white shadow-lg rounded-xl border border-gray-100">
                    <Typography variant="h6" className="mb-4 text-[#2a2e30] font-semibold">Filters</Typography>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
                        <Input
                            label="Search content"
                            value={filters.content}
                            onChange={(e) => handleFilterChange('content', e.target.value)}
                            icon={<FaSearch />}
                        />
                        <Select
                            label="Status"
                            value={filters.status}
                            onChange={(value) => handleFilterChange('status', value || '')}
                        >
                            <Option value="">All Status</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="posted">Posted</Option>
                            <Option value="failed">Failed</Option>
                            <Option value="used">Used</Option>
                        </Select>
                        <Select
                            label="Posting Status"
                            value={filters.posting_status}
                            onChange={(value) => handleFilterChange('posting_status', value || '')}
                        >
                            <Option value="">All Posting Status</Option>
                            <Option value="posted">Posted</Option>
                            <Option value="failed">Failed</Option>
                            <Option value="pending">Pending</Option>
                        </Select>
                        <Input
                            label="Message Type"
                            value={filters.message_type}
                            onChange={(e) => handleFilterChange('message_type', e.target.value)}
                        />
                        <Input
                            label="Car ID"
                            value={filters.car_id}
                            onChange={(e) => handleFilterChange('car_id', e.target.value)}
                        />
                        <Input
                            label="Driver ID"
                            value={filters.driver_id}
                            onChange={(e) => handleFilterChange('driver_id', e.target.value)}
                        />
                        <Input
                            label="Min Amount"
                            type="number"
                            value={filters.amount_min}
                            onChange={(e) => handleFilterChange('amount_min', e.target.value)}
                        />
                        <Input
                            label="Max Amount"
                            type="number"
                            value={filters.amount_max}
                            onChange={(e) => handleFilterChange('amount_max', e.target.value)}
                        />
                        <Input
                            type="date"
                            label="Date From"
                            value={filters.date_from}
                            onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        />
                        <Input
                            type="date"
                            label="Date To"
                            value={filters.date_to}
                            onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={clearFilters}
                            variant="outlined"
                            size="sm"
                            className="bg-white text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case"
                        >
                            Clear All
                        </Button>
                    </div>
                </Card>
            )}
        </>
    );
};

export default TransMessagesFilters;
